import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import SelectMenu, { type SelectMenuOption } from '../base/SelectMenu';
import SegmentedControl from '../base/SegmentedControl';
import DomainIcon from '../base/icons/domain/DomainIcon';
import SaudiRiyalSymbol from '../base/icons/SaudiRiyalSymbol';
import PresetNumberField from './PresetNumberField';
import {
  getSearchFilterFields,
  getVisibleRangeKeys,
  isInvertedRange,
  type SearchCategory,
  type SearchRangeBounds,
  type SearchRangeKey,
  type SearchRanges,
} from '../../config/searchFilterFields';
import {
  BATHROOM_PRESETS,
  BEDROOM_PRESETS,
  DEFAULT_RENT_PRICE_PERIOD,
  getAreaPresets,
  getPricePresets,
  presetLabel,
  presetsAbove,
  type RentPricePeriod,
  type SearchListingType,
} from '../../config/searchPresets';
import { formatAmount } from '../../utils/numberFormat';
import { cx, useNsText } from './searchUi';

/**
 * The «من / إلى» range filters for a search: bedrooms, bathrooms, price and area,
 * shown only where they apply to the chosen type (config/searchFilterFields — land
 * has no bedrooms or bathrooms and reads «مساحة الأرض»; with no type, سكني shows all
 * four and تجاري only price and area). See Apps/web/THEME.md.
 *
 * - Columns in DOM order غرف النوم، دورات المياه، السعر، المساحة, which an RTL page
 *   lays out right to left, as the owner listed them.
 * - Bedrooms / bathrooms: SelectMenu. Price / area: PresetNumberField (pick a preset
 *   or type). Price presets follow the listing type (rents for للإيجار, sale prices
 *   for للبيع, typing only for الكل). For rent, the price header carries a small
 *   شهري / سنوي pill (when `onPricePeriodChange` is given) and the presets follow it.
 * - The last value of every list reads «N+» (config/searchPresets): «من 5+» is 5 or
 *   more, «إلى 5+» is no upper limit (dropOpenEndedMaxes, where the request is built).
 * - Each «إلى» list only offers values above the «من» (counts keep the same value,
 *   for "exactly 3"). A «من» above its «إلى» marks both fields invalid and shows an
 *   inline alert; nothing is changed behind the user's back.
 * - Hidden fields are not rendered; dropping their values from a request is the
 *   caller's job (`pruneHiddenFilters`).
 */

export type RangeFilterLayout = 'matrix' | 'stacked' | 'auto';

export interface RangeFilterGridProps {
  /** '' (none), a lowercase id ('villa') or the UPPERCASE enum ('VILLA'). */
  propertyType: string;
  /** The سكني / تجاري toggle ('' or 'all' = both); decides the fields while no type is chosen. */
  category: SearchCategory;
  listingType: SearchListingType;
  value: SearchRanges;
  onChange: (key: SearchRangeKey, next: SearchRangeBounds) => void;
  /**
   * 'matrix': the fields as columns with «من» / «إلى» rows (wide panels).
   * 'stacked': one field under another, «من» / «إلى» inside each cell (sidebars, phones).
   * 'auto' (default): the matrix whenever this grid's own width fits every column.
   */
  layout?: RangeFilterLayout;
  /** Prefix for the field ids («<prefix>-price-min»); must be unique on the page. */
  idPrefix: string;
  /** The period rent prices are typed in. Default yearly. */
  pricePeriod?: RentPricePeriod;
  /** Shows the شهري / سنوي pill on the rent price column and reports a change. */
  onPricePeriodChange?: (period: RentPricePeriod) => void;
  /**
   * When given, a «اختر نوع العقار لعرض غرف النوم ودورات المياه» button appears
   * while no type is chosen and those columns are hidden (تجاري), and calls this.
   */
  onRequestType?: () => void;
  disabled?: boolean;
  className?: string;
  /**
   * Show only these ranges (still subject to the type rules: land never gets
   * bedrooms). The /search filter bar uses it for its single-purpose pills,
   * «السعر» and «الغرف». Omit for every range the type has.
   */
  only?: readonly SearchRangeKey[];
}

type Bound = 'min' | 'max';

interface Column {
  key: SearchRangeKey;
  kind: 'count' | 'amount';
  /** Visible header text. */
  label: string;
  /** The fields' name when it says more than the header (the price period beside a pill). */
  fieldLabel?: string;
  /** Unit shown after the label (area). */
  unit?: string;
  icon: ReactNode;
  /** Shown after the label in the header (the rent period pill). */
  headerExtra?: ReactNode;
  presets: readonly number[];
  /** Read to screen readers on amount fields: they also take a typed value. */
  typingHint?: string;
}

const EMPTY_BOUNDS: SearchRangeBounds = { min: '', max: '' };

// Matrix sizing in rem: the «من / إلى» label column, each column's minimum, the gap.
const MATRIX_LABEL_REM = 3;
const MATRIX_MIN_REM = { count: 7, amount: 10 } as const;
const MATRIX_GAP_REM = 0.75;
// Stacked: «من» and «إلى» share a row only while each cell gets at least this (rem).
const STACKED_MIN_REM = { count: 8, amount: 10.5 } as const;

// The icon glyph itself is large; nothing around it grows (owner, 2026-10).
const HEADER_ICON = 'h-6 w-6';
const ROW_LABEL = 'whitespace-nowrap pe-1 text-[0.875rem] text-[var(--pv-color-text-muted)]';

/** Count choices: «الكل», then the presets (above `min` for a «إلى» list), keeping an off-list current value. */
function countOptions(
  presets: readonly number[],
  anyLabel: string,
  current: string,
  min?: string,
): SelectMenuOption[] {
  const values = (min === undefined ? presets : presetsAbove(presets, min, { orEqual: true })).map(String);
  if (current && !values.includes(current)) {
    values.push(current);
    values.sort((a, b) => Number(a) - Number(b));
  }
  // The list's top preset reads «N+» (open-ended); an off-list value keeps its plain number.
  return [
    { value: '', label: anyLabel },
    ...values.map((v) => ({
      value: v,
      label: presets.includes(Number(v)) ? presetLabel(Number(v), presets, (n) => formatAmount(n) || String(n)) : formatAmount(v) || v,
    })),
  ];
}

/** A short «من» / «إلى» inside a cell; decorative, the field's name already says it. */
function BoundWord({ children }: { children: string }) {
  return <span className="text-[0.875rem] leading-none text-[var(--pv-color-text-muted)]">{children}</span>;
}

export default function RangeFilterGrid({
  propertyType,
  category,
  listingType,
  value,
  onChange,
  layout = 'auto',
  idPrefix,
  onRequestType,
  pricePeriod = DEFAULT_RENT_PRICE_PERIOD,
  onPricePeriodChange,
  disabled = false,
  className = '',
  only,
}: RangeFilterGridProps) {
  const { text: ui, isArabic } = useNsText('ui');
  const { text: propertyText } = useNsText('property');

  const fields = getSearchFilterFields(propertyType, category);
  const columns: Column[] = getVisibleRangeKeys(propertyType, category)
    .filter((key) => !only || only.includes(key))
    .map((key): Column => {
    switch (key) {
      case 'bedrooms':
        return {
          key,
          kind: 'count',
          label: isArabic ? fields.bedroomsLabelAr : fields.bedroomsLabelEn,
          icon: <DomainIcon id="bedroom" className={HEADER_ICON} />,
          presets: BEDROOM_PRESETS,
        };
      case 'bathrooms':
        return {
          key,
          kind: 'count',
          // The frozen «دورات المياه».
          label: propertyText('property.metrics.bathrooms.label', 'Bathrooms'),
          icon: <DomainIcon id="bathroom" className={HEADER_ICON} />,
          presets: BATHROOM_PRESETS,
        };
      case 'price': {
        const isRent = listingType === 'rent';
        const periodPill = isRent && onPricePeriodChange;
        return {
          key,
          kind: 'amount',
          // With the pill the period is shown by the pill itself; without it, in the label.
          label: !isRent || periodPill
            ? ui('range.price', 'Price')
            : pricePeriod === 'monthly'
              ? ui('range.priceMonthly', 'Price (monthly)')
              : ui('range.priceYearly', 'Price (yearly)'),
          // The pill shows the period visually; the fields still name it for screen readers.
          fieldLabel: isRent
            ? pricePeriod === 'monthly'
              ? ui('range.priceMonthly', 'Price (monthly)')
              : ui('range.priceYearly', 'Price (yearly)')
            : undefined,
          icon: <SaudiRiyalSymbol title="" className={HEADER_ICON} />,
          headerExtra: periodPill ? (
            <SegmentedControl<RentPricePeriod>
              size="sm"
              ariaLabel={ui('range.period.label', 'Rent period')}
              value={pricePeriod}
              onChange={onPricePeriodChange}
              options={[
                { value: 'monthly', label: ui('range.period.monthly', 'Monthly') },
                { value: 'yearly', label: ui('range.period.yearly', 'Yearly') },
              ]}
            />
          ) : undefined,
          presets: getPricePresets(listingType, pricePeriod),
          typingHint: ui('range.customAmount', 'or type an amount'),
        };
      }
      default:
        return {
          key,
          kind: 'amount',
          label: isArabic ? fields.areaLabelAr : fields.areaLabelEn,
          unit: ui('range.unitSqm', 'm²'),
          icon: <DomainIcon id="area" className={HEADER_ICON} />,
          presets: getAreaPresets(propertyType),
          typingHint: ui('range.customNumber', 'or type a number'),
        };
    }
  });

  // ── layout ──
  const rootRef = useRef<HTMLDivElement>(null);
  const [fitsMatrix, setFitsMatrix] = useState(false);
  const matrixRem =
    MATRIX_LABEL_REM + columns.reduce((sum, column) => sum + MATRIX_MIN_REM[column.kind] + MATRIX_GAP_REM, 0);

  useLayoutEffect(() => {
    if (layout !== 'auto') return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setFitsMatrix(width > 0 && width >= matrixRem * rootPx);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [layout, matrixRem]);

  const resolvedLayout = layout === 'auto' ? (fitsMatrix ? 'matrix' : 'stacked') : layout;

  // ── text ──
  const fromWord = ui('range.from', 'From');
  const toWord = ui('range.to', 'To');
  const anyLabel = ui('range.any', 'Any');
  const errorText = ui('range.minGreaterThanMax', 'Min is greater than max');
  const fieldName = (column: Column) => {
    const base = column.fieldLabel ?? column.label;
    return column.unit ? `${base} (${column.unit})` : base;
  };

  const boundsOf = (key: SearchRangeKey): SearchRangeBounds => value[key] ?? EMPTY_BOUNDS;
  const cellId = (key: SearchRangeKey, bound: Bound) => `${idPrefix}-${key}-${bound}`;
  const errorId = (key: SearchRangeKey) => `${idPrefix}-${key}-error`;

  const update = (key: SearchRangeKey, bound: Bound, next: string) => {
    const current = boundsOf(key);
    if (current[bound] === next) return;
    onChange(key, { ...current, [bound]: next });
  };

  const renderCell = (column: Column, bound: Bound, withPrefix: boolean) => {
    const bounds = boundsOf(column.key);
    const inverted = isInvertedRange(bounds);
    const word = bound === 'min' ? fromWord : toWord;
    const name = ui('range.boundLabel', '{{field}}: {{bound}}', { field: fieldName(column), bound: word });
    const shared = {
      id: cellId(column.key, bound),
      label: name,
      hideLabel: true,
      invalid: inverted,
      'aria-describedby': inverted ? errorId(column.key) : undefined,
      disabled,
    };

    if (column.kind === 'count') {
      return (
        <SelectMenu
          {...shared}
          value={bounds[bound]}
          onChange={(next) => update(column.key, bound, next)}
          options={countOptions(column.presets, anyLabel, bounds[bound], bound === 'max' ? bounds.min : undefined)}
          leadingIcon={withPrefix ? <BoundWord>{word}</BoundWord> : undefined}
          popoverMinWidth="8rem"
        />
      );
    }
    return (
      <PresetNumberField
        {...shared}
        value={bounds[bound]}
        onChange={(next) => update(column.key, bound, next)}
        presets={bound === 'max' ? presetsAbove(column.presets, bounds.min) : column.presets}
        placeholder={anyLabel}
        prefix={withPrefix ? word : undefined}
        typingHint={column.typingHint}
        openEndedTop
      />
    );
  };

  const renderHeader = (column: Column) => (
    <div
      data-range-header={column.key}
      className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5 text-[0.9375rem] leading-snug text-[var(--pv-color-text)]"
    >
      <span className="inline-flex shrink-0 text-[var(--pv-color-primary)]" aria-hidden="true">
        {column.icon}
      </span>
      <span className="min-w-0">
        {column.label}
        {column.unit && (
          <span className="ms-1 text-[0.875rem] text-[var(--pv-color-text-muted)]">({column.unit})</span>
        )}
      </span>
      {column.headerExtra}
    </div>
  );

  const renderError = (column: Column) =>
    isInvertedRange(boundsOf(column.key)) ? (
      <p
        id={errorId(column.key)}
        role="alert"
        className="flex items-start gap-1.5 text-[0.875rem] leading-snug text-[#C0322A] dark:text-[#FF8A80]"
      >
        <i className="ri-error-warning-line mt-0.5 text-[1rem] leading-none" aria-hidden="true" />
        <span>{errorText}</span>
      </p>
    ) : null;

  const typeChosen = Boolean(propertyType && propertyType.trim());
  const showHint = Boolean(onRequestType) && !typeChosen && (!fields.bedrooms || !fields.bathrooms);
  const hint = showHint ? (
    <button
      type="button"
      onClick={onRequestType}
      disabled={disabled}
      className={cx(
        'mt-3 inline-flex min-h-[2.75rem] items-center gap-2 rounded-lg px-1 text-start',
        'text-[0.875rem] text-[var(--pv-color-primary)] underline-offset-4 hover:underline',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:ring-[#3FBBA2]/25',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      <i className="ri-information-line text-[1rem] leading-none" aria-hidden="true" />
      <span>{ui('range.chooseTypeHint', 'Choose a property type to filter bedrooms and bathrooms')}</span>
    </button>
  ) : null;

  const anyInverted = columns.some((column) => isInvertedRange(boundsOf(column.key)));

  if (resolvedLayout === 'matrix') {
    return (
      <div ref={rootRef} data-layout="matrix" className={cx('w-full min-w-0', className)}>
        <div
          className="grid items-center gap-x-3 gap-y-2"
          style={{ gridTemplateColumns: `auto repeat(${columns.length}, minmax(0, 1fr))` }}
        >
          <span aria-hidden="true" />
          {columns.map((column) => (
            <div key={column.key} className="min-w-0 self-end pb-0.5">
              {renderHeader(column)}
            </div>
          ))}
          <span className={ROW_LABEL} aria-hidden="true">
            {fromWord}
          </span>
          {columns.map((column) => (
            <div key={column.key} className="min-w-0">
              {renderCell(column, 'min', false)}
            </div>
          ))}
          <span className={ROW_LABEL} aria-hidden="true">
            {toWord}
          </span>
          {columns.map((column) => (
            <div key={column.key} className="min-w-0">
              {renderCell(column, 'max', false)}
            </div>
          ))}
          {anyInverted && (
            <>
              <span aria-hidden="true" />
              {columns.map((column) => (
                <div key={column.key} className="min-w-0 self-start">
                  {renderError(column)}
                </div>
              ))}
            </>
          )}
        </div>
        {hint}
      </div>
    );
  }

  return (
    <div ref={rootRef} data-layout="stacked" className={cx('w-full min-w-0 space-y-4', className)}>
      {columns.map((column) => (
        <div key={column.key} className="min-w-0">
          <div className="mb-2">{renderHeader(column)}</div>
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${STACKED_MIN_REM[column.kind]}rem), 1fr))`,
            }}
          >
            {renderCell(column, 'min', true)}
            {renderCell(column, 'max', true)}
          </div>
          {isInvertedRange(boundsOf(column.key)) && <div className="mt-1.5">{renderError(column)}</div>}
        </div>
      ))}
      {hint}
    </div>
  );
}
